export const PLATFORM_ORDER = ["youtube", "instagram", "tiktok", "facebook"];

export const PLATFORM_LABELS = {
  youtube: "YouTube",
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
};

export const fallbackMediaKitStats = [
  {
    platform: "youtube",
    followersCount: 660000,
    followersCapturedAt: "2026-06-20T00:00:00Z",
    views28d: 8100000,
    discoveryPercent: 91.3,
    femalePercent: null,
    age1844Percent: null,
    countries: [],
    periodStart: "2026-05-22",
    periodEnd: "2026-06-20",
    capturedAt: "2026-06-20T00:00:00Z",
    source: "media_kit_baseline",
  },
  {
    platform: "instagram",
    followersCount: 431000,
    followersCapturedAt: "2026-06-20T00:00:00Z",
    views28d: 4000000,
    discoveryPercent: 85.8,
    femalePercent: 83.9,
    age1844Percent: null,
    countries: [],
    periodStart: "2026-05-22",
    periodEnd: "2026-06-20",
    capturedAt: "2026-06-20T00:00:00Z",
    source: "media_kit_baseline",
  },
  {
    platform: "tiktok",
    followersCount: 355000,
    followersCapturedAt: "2026-06-20T00:00:00Z",
    views28d: 281000,
    discoveryPercent: null,
    femalePercent: null,
    age1844Percent: null,
    countries: [],
    periodStart: "2026-05-22",
    periodEnd: "2026-06-20",
    capturedAt: "2026-06-20T00:00:00Z",
    source: "media_kit_baseline",
  },
  {
    platform: "facebook",
    followersCount: 231000,
    followersCapturedAt: "2026-06-20T00:00:00Z",
    views28d: 1200000,
    discoveryPercent: null,
    femalePercent: null,
    age1844Percent: null,
    countries: [],
    periodStart: "2026-05-22",
    periodEnd: "2026-06-20",
    capturedAt: "2026-06-20T00:00:00Z",
    source: "media_kit_baseline",
  },
];

const finiteValues = (values) => values.filter((value) => Number.isFinite(value));

const average = (values) => {
  const available = finiteValues(values);
  return available.length ? available.reduce((sum, value) => sum + value, 0) / available.length : null;
};

const countryPercent = (stat, code) => {
  const match = stat.countries?.find((country) => country.code?.toUpperCase() === code);
  return Number.isFinite(match?.percent) ? match.percent : null;
};

export function buildMediaKitSummary(stats) {
  const available = PLATFORM_ORDER
    .map((platform) => stats.find((stat) => stat.platform === platform))
    .filter(Boolean);
  const followers = finiteValues(available.map((stat) => stat.followersCount));
  const views = finiteValues(available.map((stat) => stat.views28d));
  const unitedStates = finiteValues(available.map((stat) => countryPercent(stat, "US")));
  const supportingCountries = new Map();

  for (const stat of available) {
    for (const country of stat.countries || []) {
      if (!country.code || country.code.toUpperCase() === "US" || !Number.isFinite(country.percent)) continue;
      const key = country.code.toUpperCase();
      const current = supportingCountries.get(key) || { ...country, score: 0, appearances: 0 };
      current.score += country.percent;
      current.appearances += 1;
      supportingCountries.set(key, current);
    }
  }

  const updatedTimes = available
    .flatMap((stat) => [Date.parse(stat.capturedAt), Date.parse(stat.followersCapturedAt)])
    .filter(Number.isFinite);

  return {
    totalFollowers: followers.reduce((sum, value) => sum + value, 0),
    totalViews28d: views.reduce((sum, value) => sum + value, 0),
    platformCount: available.length,
    femalePercent: average(available.map((stat) => stat.femalePercent)),
    age1844Percent: average(available.map((stat) => stat.age1844Percent)),
    unitedStatesRange: unitedStates.length
      ? { min: Math.min(...unitedStates), max: Math.max(...unitedStates) }
      : null,
    supportingCountries: [...supportingCountries.values()]
      .sort((left, right) => right.appearances - left.appearances || right.score - left.score)
      .slice(0, 3),
    latestUpdatedAt: updatedTimes.length ? new Date(Math.max(...updatedTimes)).toISOString() : null,
  };
}

export function formatCompactNumber(value) {
  if (!Number.isFinite(value)) return "Unavailable";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: value >= 1000000 && value < 10000000 ? 2 : value >= 1000000 ? 1 : 0,
  }).format(value);
}

export function formatPercent(value, suffix = "%") {
  if (!Number.isFinite(value)) return "Unavailable";
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}${suffix}`;
}

export function formatUpdatedDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function formatUnitedStatesRange(range) {
  if (!range) return "Unavailable";
  const min = Math.round(range.min);
  const max = Math.round(range.max);
  return min === max ? `${min}%` : `${min}–${max}%`;
}
